import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { supabase } from "../../../supabase";
import { useAuth } from "../../../contexts/AuthContext";

export const UserContext = createContext();

/**
 * Custom hook to use the UserContext
 */
export function useUser() {
  return useContext(UserContext);
}

/**
 * Provider component that wraps the users feature and provides user state and functions
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function UserProvider({ children }) {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Get API base URL
  const getApiBaseUrl = () => {
    return process.env.NODE_ENV === "development"
      ? "http://localhost:4000/"
      : "https://staging-tables-split-92ccb043d150.herokuapp.com/";
  };

  // Fetch users under the current user's hierarchy
  const fetchUsers = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_master")
        .select("*")
        .eq("parent_id", currentUser.id)
        .eq("refs_user_lifecycle_status_id", 2); // Active users

      if (fetchError) throw fetchError;

      setUsers(data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    if (!currentUser) return;

    try {
      const { data, error: fetchError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_master")
        .select(
          `
          *,
          parent:submitted_by(first_name, middle_name, surname)
        `
        )
        .eq("refs_user_lifecycle_status_id", 1); // Pending approval

      if (fetchError) throw fetchError;

      setPendingApprovals(data || []);
    } catch (err) {
      console.error("Error fetching pending approvals:", err);
    }
  }, [currentUser]);

  // Fetch a specific user by ID
  const fetchUserById = async id => {
    // Validate that id is a valid integer before proceeding
    // eslint-disable-next-line no-restricted-globals
    if (!id || isNaN(parseInt(id))) {
      setError("Invalid user ID");
      return;
    }

    const userId = parseInt(id);
    setLoading(true);
    setError(null);

    try {
      // Get access token
      const { data: accessToken } = await supabase.auth.getSession();
      const token = accessToken?.session?.access_token;

      if (!token) {
        throw new Error("No access token available");
      }

      // Call API endpoint instead of direct database access
      const response = await fetch(`${getApiBaseUrl()}api/users/${userId}?access_token=${token}`);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch user");
      }

      // Set the user data (API already includes banking details)
      setSelectedUser(result.data);
    } catch (err) {
      console.error("Error fetching user:", err);
      setError(`Error loading user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update user status
  const updateUserStatus = useCallback(
    async (userId, statusId, reason = null) => {
      setLoading(true);
      setError(null);

      try {
        const { error: updateError } = await supabase
          .schema(process.env.REACT_APP_SB_SCHEMA)
          .from("users_master")
          .update({
            refs_user_lifecycle_status_id: statusId,
            decline_reason: reason,
            last_updated_by_auth_id: currentUser?.auth_id,
          })
          .eq("id", userId);

        if (updateError) throw updateError;

        // If updating the currently selected user, refresh their data
        if (selectedUser && selectedUser.id === userId) {
          await fetchUserById(userId);
        }

        // Refresh data based on the updated status
        if (statusId === 2) {
          // Approved
          await fetchUsers();
        }
        await fetchPendingApprovals();

        return true;
      } catch (err) {
        console.error("Error updating user status:", err);
        setError(err.message || "Failed to update user status");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      currentUser,
      fetchUsers,
      fetchPendingApprovals,
      fetchUserById,
      selectedUser,
    ]
  );

  // Set user to active
  const setUserActive = useCallback(
    async userId => {
      return updateUserStatus(userId, 2); // 2 = Active
    },
    [updateUserStatus]
  );

  // Set user to inactive
  const setUserInactive = useCallback(
    async userId => {
      return updateUserStatus(userId, 3); // 3 = Inactive
    },
    [updateUserStatus]
  );

  // Delete user (mark as deleted)
  const deleteUser = useCallback(
    async userId => {
      return updateUserStatus(userId, 4); // 4 = Deleted
    },
    [updateUserStatus]
  );

  // Update user information
  const updateUser = useCallback(
    async (userId, userData) => {
      setLoading(true);
      setError(null);

      try {
        // Get access token for API call
        const { data: accessToken } = await supabase.auth.getSession();
        const token = accessToken?.session?.access_token;

        if (!token) {
          throw new Error("No access token available");
        }

        const updateData = {
          // Personal information
          firstName: userData.firstName,
          middleName: userData.middleName,
          surname: userData.surname,
          dateOfBirth: userData.dateOfBirth,
          refsGender: userData.refsGender,
          refsIdentityDocType: userData.refsIdentityDocType,
          identificationNo: userData.identificationNo,
          nationality: userData.nationality,
          operatorId: userData.operatorId,
          companyName: userData.companyName,

          // Contact information
          email: userData.email,
          phone: userData.phone,
          altPhone: userData.altPhone,

          // Address information
          province: userData.province,
          street: userData.street,
          suburb: userData.suburb,
          city: userData.city,
          postalCode: userData.postalCode,
          country: userData.country,

          // Payment information
          refsPaymentType: userData.refsPaymentType,
          refsBankAccountType: userData.refsBankAccountType,
          eWalletLink: userData.eWalletLink,

          // Document URLs
          idPhotoUrl: userData.idPhotoUrl,
          personPhotoUrl: userData.personPhotoUrl,

          // Passport informatio
          passportIssueDate: userData.passportIssueDate,
          passportExpiryDate: userData.passportExpiryDate,

          // Banking detail
          bank: userData.bank,
          bankAccountNumber: userData.bankAccountNumber,
          branchCode: userData.branchCode,

          // System fields
          refsShirtSize: userData.refsShirtSize,
          parentId: userData.parentId,
          refsTypeId: userData.refsTypeId,
        };

        // Call the API endpoint
        const response = await fetch(`${getApiBaseUrl()}api/users/${userId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || "Failed to update user");
        }

        // Refresh the user data
        await fetchUserById(userId);

        return true;
      } catch (err) {
        setError(err.message || "Failed to update user");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchUserById]
  );

  // Load initial data
  useEffect(() => {
    if (currentUser) {
      fetchUsers();
      fetchPendingApprovals();
    }
  }, [currentUser, fetchUsers, fetchPendingApprovals]);

  // Context value
  const value = useMemo(
    () => ({
      users,
      loading,
      error,
      pendingApprovals,
      selectedUser,
      fetchUsers,
      fetchPendingApprovals,
      fetchUserById,
      updateUserStatus,
      setUserActive,
      setUserInactive,
      deleteUser,
      updateUser,
      setSelectedUser,
    }),
    [
      users,
      loading,
      error,
      pendingApprovals,
      selectedUser,
      fetchUsers,
      fetchPendingApprovals,
      fetchUserById,
      updateUserStatus,
      setUserActive,
      setUserInactive,
      deleteUser,
      updateUser,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
